/* eslint-disable jsx-a11y/no-noninteractive-element-to-interactive-role */

/* eslint-disable jsx-a11y/no-noninteractive-element-interactions */
import { Builder } from '@builder.io/react';
import React, { useMemo, useState } from 'react';
import sanitizeHtml from 'sanitize-html';
import { twMerge } from 'tailwind-merge';

import ChevronDown from '@videoblocks/react-icons/ChevronDown';

import { Button } from '../../storyblocks-design-system/components/atoms/Button/Button';
import { Text } from '../../storyblocks-design-system/components/atoms/Text/Text';
import { generateFAQSchema } from '../../utils/structured-data/faqSchemaGenerator';
import StructuredData from '../elements/StructuredData';

interface IFaqItem {
  faqQuestion: string;
  faqAnswer: string;
}

interface IFaqSeoProps {
  faqItems?: IFaqItem[];
  showButton?: boolean;
  buttonLabel?: string;
  buttonUrl?: string;
}

const DEFAULT_BUTTON_URL = 'https://help.storyblocks.com/en';

const sanitizeConfig: sanitizeHtml.IOptions = {
  allowedTags: ['p', 'ul', 'ol', 'li', 'a', 'strong', 'span', 'em'],
  allowedAttributes: {
    a: ['href', 'target', 'rel'],
  },
  allowedSchemes: ['http', 'https', 'mailto'],
};

const FaqSeo = ({
  faqItems = [],
  showButton = true,
  buttonLabel = 'See All FAQs',
  buttonUrl = DEFAULT_BUTTON_URL,
}: IFaqSeoProps) => {
  const [openItem, setOpenItem] = useState<string | null>(null);

  const faqStructuredData = useMemo(() => {
    if (!faqItems.length) return null;
    return generateFAQSchema({
      faqItems,
      title: 'Frequently Asked Questions',
      organizationName: 'Storyblocks',
    });
  }, [faqItems]);

  const handleOnToggle = (section: string) => {
    setOpenItem((prev) => (prev === section ? null : section));
  };

  const handleKeyDown = (e: React.KeyboardEvent, section: string) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      handleOnToggle(section);
    }
  };

  if (!faqItems.length) return null;

  return (
    <>
      {faqStructuredData && <StructuredData data={faqStructuredData} />}
      <div className="py-16 md:py-24 px-6 md:px-12 lg:px-24">
        <div
          className="font-polysans flex flex-col lg:flex-row w-full mx-auto gap-8"
          style={{ maxWidth: '1440px' }}
        >
          <div className="lg:w-1/3 lg:flex-none">
            <Text
              fontFamily="polysans"
              fontSize="4xl"
              fontWeight="semibold"
              className="text-3xl md:text-4xl"
            >
              <>
                <span className="lg:block">Frequently Asked Questions</span>
              </>
            </Text>
          </div>

          <div className="flex-auto flex flex-col items-center">
            <ul className="w-full space-y-6 mb-0">
              {faqItems.map(({ faqAnswer, faqQuestion }, index) => (
                <li
                  className={twMerge(
                    'bg-gray-200 rounded-3xl border-gray-300 border-2',
                    'flex flex-col w-full p-6 lg:p-8 text-left',
                    'cursor-pointer transition-colors duration-150 rounded-3xl',
                    'focus-visible:outline-white focus-visible:ring-blue-600 focus-visible:ring-2',
                    'active:bg-gray-300 active:border-gray-500',
                    'touch-manipulation'
                  )}
                  onClick={() => handleOnToggle(`faq_${index}`)}
                  onKeyDown={(e) => handleKeyDown(e, `faq_${index}`)}
                  aria-controls={`faq-seo-content-${index}`}
                  id={`faq-seo-question-${index}`}
                  key={`faq_${index}`}
                  role="button"
                  tabIndex={0}
                >
                  <div className="flex w-full justify-between items-center">
                    <Text
                      className="text-gray-1000 my-0 pr-4 text-xl leading-7 lg:text-2xl lg:leading-normal"
                      fontFamily="polysans"
                      fontSize="2xl"
                      fontWeight="semibold"
                      asChild
                    >
                      <h3>{faqQuestion}</h3>
                    </Text>
                    <div className="flex flex-none justify-center items-center w-8 h-8 bg-yellow-300 rounded-full border-2 border-blue-900 transition-transform duration-200">
                      <ChevronDown
                        className={twMerge(
                          'h-4 w-4 transition-transform duration-200 ease-in-out',
                          openItem === `faq_${index}` && 'transform rotate-180'
                        )}
                        aria-hidden="true"
                      />
                    </div>
                  </div>
                  <div
                    id={`faq-seo-content-${index}`}
                    className={twMerge(
                      openItem === `faq_${index}`
                        ? 'max-h-[1000px] pt-4'
                        : 'max-h-0',
                      'overflow-hidden transition-[max-height] duration-200 ease-in-out'
                    )}
                    role="region"
                    aria-live="polite"
                    aria-labelledby={`faq-seo-question-${index}`}
                  >
                    <div
                      className="text-gray-800 font-polysans text-base leading-snug lg:text-xl lg:leading-[27px]"
                      dangerouslySetInnerHTML={{
                        __html: sanitizeHtml(faqAnswer, sanitizeConfig),
                      }}
                    />
                  </div>
                </li>
              ))}
            </ul>
            {showButton && (
              <Button
                href={buttonUrl}
                variant="secondary"
                size="large"
                target="_blank"
                rel="noopener noreferrer"
                className="mt-8 md:mt-12"
              >
                {buttonLabel}
              </Button>
            )}
          </div>
        </div>
      </div>
    </>
  );
};

export default FaqSeo;

Builder.registerComponent(FaqSeo, {
  name: 'Faq SEO',
  inputs: [
    {
      name: 'faqItems',
      type: 'list',
      required: true,
      helperText: 'List of questions and answers',
      defaultValue: [
        {
          faqQuestion: 'Question',
          faqAnswer: 'Answer',
        },
      ],
      subFields: [
        {
          name: 'faqQuestion',
          type: 'string',
          required: true,
          helperText: 'Question',
          defaultValue: 'Question',
        },
        {
          name: 'faqAnswer',
          type: 'richText',
          required: true,
          helperText: 'Answer (supports rich text)',
          defaultValue: 'Answer',
        },
      ],
    },
    {
      name: 'showButton',
      type: 'boolean',
      helperText: 'Show or hide the "See All FAQs" button',
      defaultValue: true,
    },
    {
      name: 'buttonLabel',
      type: 'string',
      helperText: 'Button label',
      defaultValue: 'See All FAQs',
      showIf: `options.get('showButton') === true`,
    },
    {
      name: 'buttonUrl',
      type: 'url',
      helperText:
        'Button destination URL (supports additional query parameters)',
      defaultValue: 'https://help.storyblocks.com/en',
      showIf: `options.get('showButton') === true`,
    },
  ],
});
